import { Document } from 'services/document/document.types';
import { User } from 'types/User';

export type PersonProfile = {
    firstname: string;
    lastname: string;
    born_date: string;
    first_contact_date?: string;
    email?: string;
    phone: string;
    address?: Address;
    observation?: string;
    source?: string;
};

export type Address = {
    address: string;
    address_add?: string;
    city: string;
    zipcode: string;
    country: string;
    formatted?: string;
};

export type Disorder = {
    mdph: string;
    main: string;
    secondaries: Array<string>;
};

export type Stakeholder = {
    fullname?: string;
    id?: number;
    main_contact: boolean;
    legal_contact: boolean;
    firstname: string;
    lastname: string;
    phone: string;
    email: string;
    status: string;
    address: Address;
    observation: string;
};

export type Project = {
    reference: string;
    id: number;
    status: string;
    type: string;
    initial_request: string;
    total_events_duration: number;
    label: string;
    created_at: string;
};

export enum InformativeReunionStatus {
    PENDING = 'pending', // Entrée en attente
    WAITING = 'waiting', // Souhaite réfléchir
    NO_FOLLOWUP = 'no_followup' // Sans suite
}

export type InformativeReunion = {
    done: boolean;
    date?: string;
    supervisor?: User;
    appv?: User;
    location?: string;
    department?: string;
    status?: InformativeReunionStatus;
};

export type CreatePersonPayload = {
    firstname: string;
    lastname: string;
    phone: string;
    born_date?: string;
    first_contact_date?: string;
    genre?: string;
    email?: string;
    address?: Address;
    observation?: string;
    departments?: string[];
    full_add?: string | number | readonly string[] | undefined;
    source?: string;
    initial_request?: string;
    informative_reunion?: InformativeReunion;
    label?: string;
    stakeholders?: Array<Stakeholder>;
    documents?: Array<Document>;
    disorders?: Disorder;
    responsible_person?: User;
    send_access_link?: boolean;
};

export type CreatePersonResponse = {
    id: number;
    reference: string;
    fullname: string;
    firstname: string;
    lastname: string;
    phone: string;
    born_date: string;
    first_contact_date?: string;
    genre: string;
    email: string;
    address: Address;
    observation: string;
    source: string;
    role?: string;
    informative_reunion?: InformativeReunion;
    status: string;
    departments?: string[];
    initial_request?: string;
    stakeholders: Array<Stakeholder>;
    documents: Array<Document>;
    disorders: Disorder;
    projects: Array<Project>;
    created_at: string;
    updated_at?: string;
    responsible_person?: User;
    is_outgoing: boolean;
    outgoingAt: boolean;
    outgoingReason?: string;
};

export type Folder = {
    reference: string;
    id: number;
    status: string;
    type: string;
    created_at: string;
};

export type GetPersonPayload = {
    id: number | string;
};

export type GetPersonStakeholderPayload = {
    id: number | string;
    stakeholderId: number | string;
};

export type GetPersonsResponse = Array<CreatePersonResponse>;

export type UpdatePersonPayload = {
    id: number;
    body: {
        firstname: string;
        lastname: string;
        born_date?: string;
        email?: string;
        phone: string;
        address?: Address;
        observation?: string;
        source?: string;
        departments?: string[];
        disorders?: Disorder;
        informative_reunion?: InformativeReunion;
        responsible_person?: User;
    };
};

export type UpdateDisordersPayload = {
    id: number;
    body: Disorder;
};

export type UpdateResponsiblePersonPayload = {
    id: number;
    body: {
        id: number;
    };
};

export type UpdateOutgoingPayload = {
    id: number;
    body: {
        reason: string;
        outgoing: boolean;
    };
};

export type CreateProjectPayload = {
    id: number;
    label: string;
    initial_request: string;
};

export type UpdateProjectPayload = {
    id: number;
    project_id: number;
    label: string;
    initial_request: string;
};

export type UpdatePersonStatusPayload = {
    id: number;
    status: string;
};

export type GetPersonResponse = CreatePersonResponse;
export type GetPersonStakeholderResponse = Stakeholder;
export type PersonDetails = CreatePersonResponse;

export type Person = CreatePersonResponse;
